import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Shield, Scale, Zap, Settings, Building, Globe } from 'lucide-react';
import { SalesQualificationStepProps } from '@/types/onboarding/salesQualification';
import { createArrayUpdateHelper } from '../utils';

export const EnterpriseRequirements: React.FC<SalesQualificationStepProps> = ({
  data,
  onUpdate,
}) => {
  const updateFormData = (field: string, value: unknown) => {
    onUpdate({ ...data, [field]: value });
  };

  const handleArrayUpdate = createArrayUpdateHelper(data, updateFormData);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Enterprise Requirements</h2>
        <p className="text-gray-600">
          Define your enterprise-specific compliance, security, and scalability needs
        </p>
      </div>

      {/* Compliance Requirements */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            Compliance & Regulatory Requirements
          </CardTitle>
          <CardDescription>
            What compliance standards and regulations must your solution meet? (Check all that
            apply)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              'SOC 2 Type II compliance',
              'GDPR compliance (European data protection)',
              'CCPA compliance (California privacy laws)',
              'HIPAA compliance (healthcare data protection)',
              'PCI DSS compliance (payment card security)',
              'FERPA compliance (educational records)',
              'State-specific data protection regulations',
              'Local government compliance requirements',
              'Industry-specific compliance standards',
              'Fair Housing Act compliance',
              'ADA accessibility compliance (WCAG 2.1)',
              'Financial services regulations',
              'Data residency and sovereignty requirements',
              'Cross-border data transfer restrictions',
              'Audit trail and record retention requirements',
              'Custom compliance frameworks',
            ].map((compliance) => (
              <div key={compliance} className="flex items-start space-x-2">
                <Checkbox
                  id={`compliance-${compliance}`}
                  checked={data.complianceRequirements?.includes(compliance) || false}
                  onCheckedChange={(checked) =>
                    handleArrayUpdate('complianceRequirements', compliance, !!checked)
                  }
                />
                <Label htmlFor={`compliance-${compliance}`} className="text-sm leading-relaxed">
                  {compliance}
                </Label>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <Label htmlFor="customCompliance">Additional compliance requirements or details</Label>
            <Textarea
              id="customCompliance"
              value={data.customCompliance || ''}
              onChange={(e) => updateFormData('customCompliance', e.target.value)}
              placeholder="e.g., Specific industry regulations, local government requirements, internal policy compliance..."
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Security Requirements */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="w-5 h-5 text-red-600" />
            Security & Data Protection Requirements
          </CardTitle>
          <CardDescription>
            What security features and protections are essential for your enterprise? (Check all
            that apply)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              'Multi-factor authentication (MFA/2FA)',
              'Single Sign-On (SSO) integration',
              'Advanced role-based access control (RBAC)',
              'Attribute-based access control (ABAC)',
              'Data encryption at rest (AES-256)',
              'Data encryption in transit (TLS 1.3)',
              'End-to-end encryption for sensitive data',
              'Regular third-party security audits',
              'Penetration testing and vulnerability assessments',
              'Security Information and Event Management (SIEM)',
              'Real-time threat detection and monitoring',
              'Data loss prevention (DLP) capabilities',
              'Secure API gateway with rate limiting',
              'IP whitelisting and geographic restrictions',
              'Secure backup and disaster recovery',
              'Zero-trust network architecture',
              'Custom security protocols and policies',
              'On-premise or private cloud deployment options',
              'Air-gapped deployment capability',
              'Hardware security module (HSM) integration',
            ].map((security) => (
              <div key={security} className="flex items-start space-x-2">
                <Checkbox
                  id={`security-${security}`}
                  checked={data.securityRequirements?.includes(security) || false}
                  onCheckedChange={(checked) =>
                    handleArrayUpdate('securityRequirements', security, !!checked)
                  }
                />
                <Label htmlFor={`security-${security}`} className="text-sm leading-relaxed">
                  {security}
                </Label>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <Label htmlFor="securityConcerns">Specific security concerns or requirements</Label>
            <Textarea
              id="securityConcerns"
              value={data.securityConcerns || ''}
              onChange={(e) => updateFormData('securityConcerns', e.target.value)}
              placeholder="e.g., Previous security incidents, specific threat models, regulatory security requirements..."
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Scalability & Performance */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Scale className="w-5 h-5 text-green-600" />
              Scalability & Performance
            </CardTitle>
            <CardDescription>How must your solution scale and perform?</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="scalabilityNeeds">Expected Scalability Pattern</Label>
              <Select
                value={data.scalabilityNeeds || ''}
                onValueChange={(value) => updateFormData('scalabilityNeeds', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select scalability needs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stable">Stable - no significant scaling expected</SelectItem>
                  <SelectItem value="moderate">
                    Moderate scaling (2-5x growth over 5 years)
                  </SelectItem>
                  <SelectItem value="high">High scaling (5-20x growth over 5 years)</SelectItem>
                  <SelectItem value="massive">Massive scaling (20x+ growth potential)</SelectItem>
                  <SelectItem value="global">
                    Global scaling with multi-region deployment
                  </SelectItem>
                  <SelectItem value="elastic">Elastic scaling based on usage patterns</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-medium">
                Performance Requirements (Check all that apply)
              </Label>
              <div className="space-y-2 mt-2">
                {[
                  '99.9% uptime SLA guarantee',
                  '99.99% uptime SLA guarantee',
                  'Sub-second response times for all operations',
                  'High-performance database optimization',
                  'Content delivery network (CDN) integration',
                  'Auto-scaling based on traffic patterns',
                  'Load balancing across multiple servers',
                  'Database replication and failover',
                  'Real-time data synchronization',
                  'Offline capability and sync',
                  'Mobile-optimized performance',
                  'Global performance optimization',
                ].map((performance) => (
                  <div key={performance} className="flex items-start space-x-2">
                    <Checkbox
                      id={`performance-${performance}`}
                      checked={data.performanceRequirements?.includes(performance) || false}
                      onCheckedChange={(checked) =>
                        handleArrayUpdate('performanceRequirements', performance, !!checked)
                      }
                    />
                    <Label
                      htmlFor={`performance-${performance}`}
                      className="text-sm leading-relaxed"
                    >
                      {performance}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Integration Complexity */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-600" />
              Integration Architecture
            </CardTitle>
            <CardDescription>What level of integration complexity do you need?</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="integrationComplexity">Integration Complexity Level</Label>
              <Select
                value={data.integrationComplexity || ''}
                onValueChange={(value) => updateFormData('integrationComplexity', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select integration complexity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="simple">Simple - Standard API connections only</SelectItem>
                  <SelectItem value="moderate">
                    Moderate - Some custom middleware and transformations
                  </SelectItem>
                  <SelectItem value="complex">
                    Complex - Multiple legacy systems and custom protocols
                  </SelectItem>
                  <SelectItem value="enterprise">
                    Enterprise - Full ecosystem integration with real-time sync
                  </SelectItem>
                  <SelectItem value="custom">
                    Custom - Proprietary systems requiring specialized development
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-medium">
                Integration Capabilities Needed (Check all that apply)
              </Label>
              <div className="space-y-2 mt-2">
                {[
                  'RESTful API suite with full CRUD operations',
                  'GraphQL API for flexible data queries',
                  'Real-time WebSocket connections',
                  'Event-driven architecture with webhooks',
                  'Message queue integration (RabbitMQ, Kafka)',
                  'Enterprise Service Bus (ESB) connectivity',
                  'Legacy system integration via custom adapters',
                  'File-based data exchange (CSV, XML, JSON)',
                  'Database direct integration capabilities',
                  'Microservices architecture support',
                  'Serverless function integration',
                  'Third-party SaaS platform connectors',
                ].map((capability) => (
                  <div key={capability} className="flex items-start space-x-2">
                    <Checkbox
                      id={`integration-capability-${capability}`}
                      checked={data.integrationCapabilities?.includes(capability) || false}
                      onCheckedChange={(checked) =>
                        handleArrayUpdate('integrationCapabilities', capability, !!checked)
                      }
                    />
                    <Label
                      htmlFor={`integration-capability-${capability}`}
                      className="text-sm leading-relaxed"
                    >
                      {capability}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Deployment & Infrastructure */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building className="w-5 h-5 text-indigo-600" />
            Deployment & Infrastructure Requirements
          </CardTitle>
          <CardDescription>
            Where and how should your enterprise solution be deployed?
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Preferred Deployment Model</Label>
                <Select
                  value={data.deploymentModel || ''}
                  onValueChange={(value) => updateFormData('deploymentModel', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select deployment preference" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public-cloud">
                      Public Cloud (AWS, Azure, GCP) - Fully managed
                    </SelectItem>
                    <SelectItem value="private-cloud">
                      Private Cloud - Dedicated infrastructure
                    </SelectItem>
                    <SelectItem value="hybrid-cloud">
                      Hybrid Cloud - Mix of public and private
                    </SelectItem>
                    <SelectItem value="on-premise">
                      On-Premise - Full control and data sovereignty
                    </SelectItem>
                    <SelectItem value="multi-cloud">
                      Multi-Cloud - Distributed across providers
                    </SelectItem>
                    <SelectItem value="edge-computing">
                      Edge Computing - Distributed edge nodes
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium">Geographic Requirements</Label>
                <div className="space-y-2 mt-2">
                  {[
                    'Data must remain within specific countries/regions',
                    'Multi-region deployment for redundancy',
                    'Content delivery optimization globally',
                    'Compliance with local data residency laws',
                    'Disaster recovery in different geographic regions',
                    'Edge computing for reduced latency',
                  ].map((geo) => (
                    <div key={geo} className="flex items-start space-x-2">
                      <Checkbox
                        id={`geo-${geo}`}
                        checked={data.geographicRequirements?.includes(geo) || false}
                        onCheckedChange={(checked) =>
                          handleArrayUpdate('geographicRequirements', geo, !!checked)
                        }
                      />
                      <Label htmlFor={`geo-${geo}`} className="text-sm leading-relaxed">
                        {geo}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Infrastructure Capabilities</Label>
                <div className="space-y-2">
                  {[
                    'Container orchestration (Kubernetes, Docker)',
                    'Serverless computing integration',
                    'Auto-scaling and load balancing',
                    'Infrastructure as Code (IaC) deployment',
                    'CI/CD pipeline integration',
                    'Monitoring and observability tools',
                    'Log aggregation and analysis',
                    'Backup and disaster recovery automation',
                    'Blue/green deployment capabilities',
                    'Database clustering and replication',
                    'CDN and caching optimization',
                    'Network security and firewalls',
                  ].map((infra) => (
                    <div key={infra} className="flex items-start space-x-2">
                      <Checkbox
                        id={`infra-${infra}`}
                        checked={data.infrastructureCapabilities?.includes(infra) || false}
                        onCheckedChange={(checked) =>
                          handleArrayUpdate('infrastructureCapabilities', infra, !!checked)
                        }
                      />
                      <Label htmlFor={`infra-${infra}`} className="text-sm leading-relaxed">
                        {infra}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Custom Development */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="w-5 h-5 text-orange-600" />
            Custom Development & Specialization
          </CardTitle>
          <CardDescription>
            What custom development and specialized features do you require?
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="customDevelopmentNeeds">Custom Development Requirements *</Label>
            <Textarea
              id="customDevelopmentNeeds"
              value={data.customDevelopmentNeeds || ''}
              onChange={(e) => updateFormData('customDevelopmentNeeds', e.target.value)}
              placeholder="e.g., Custom workflows for unique business processes, specialized reporting dashboards, unique integrations with proprietary systems, custom mobile applications, white-label branding requirements..."
              rows={4}
              required
            />
          </div>

          <div>
            <Label htmlFor="specializedFeatures">
              Specialized Features or Industry-Specific Needs
            </Label>
            <Textarea
              id="specializedFeatures"
              value={data.specializedFeatures || ''}
              onChange={(e) => updateFormData('specializedFeatures', e.target.value)}
              placeholder="e.g., Multi-language support, advanced analytics and AI, custom user interfaces, specialized compliance reporting, industry-specific modules..."
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="futureEnhancements">Planned Future Enhancements or Roadmap</Label>
            <Textarea
              id="futureEnhancements"
              value={data.futureEnhancements || ''}
              onChange={(e) => updateFormData('futureEnhancements', e.target.value)}
              placeholder="e.g., AI/ML integration, IoT connectivity, mobile app expansion, additional modules, advanced automation..."
              rows={3}
            />
          </div>

          <div>
            <Label className="text-sm font-medium">Development Partnership Preferences</Label>
            <div className="space-y-2 mt-2">
              {[
                'Dedicated development team assigned to our project',
                'Access to source code and development environment',
                'Regular development sprints with iterative feedback',
                'Joint development with our internal team',
                'Full white-label development with our branding',
                'Ongoing enhancement and feature development partnership',
                'Custom module development as needed',
                'API-first development for maximum flexibility',
              ].map((pref) => (
                <div key={pref} className="flex items-start space-x-2">
                  <Checkbox
                    id={`dev-pref-${pref}`}
                    checked={data.developmentPreferences?.includes(pref) || false}
                    onCheckedChange={(checked) =>
                      handleArrayUpdate('developmentPreferences', pref, !!checked)
                    }
                  />
                  <Label htmlFor={`dev-pref-${pref}`} className="text-sm leading-relaxed">
                    {pref}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Enterprise Summary */}
      <Card className="bg-linear-to-br from-purple-50 to-indigo-50 border-purple-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Globe className="w-5 h-5 text-purple-600" />
            Enterprise Implementation Summary
          </CardTitle>
          <CardDescription>
            Any additional enterprise requirements or special considerations?
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="enterprisePriorities">Top Enterprise Priorities</Label>
            <Textarea
              id="enterprisePriorities"
              value={data.enterprisePriorities || ''}
              onChange={(e) => updateFormData('enterprisePriorities', e.target.value)}
              placeholder="e.g., Security and compliance are top priority, need rapid implementation, cost optimization is critical, scalability for future growth..."
              rows={2}
            />
          </div>

          <div>
            <Label htmlFor="additionalNotes">Additional Notes or Special Requirements</Label>
            <Textarea
              id="additionalNotes"
              value={data.additionalNotes || ''}
              onChange={(e) => updateFormData('additionalNotes', e.target.value)}
              placeholder="e.g., Unique constraints, special timing considerations, specific vendor preferences, regulatory considerations..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Help Text */}
      <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
        <p className="text-sm text-violet-700">
          <strong>Enterprise requirements shape your custom solution architecture:</strong>
        </p>
        <ul className="text-sm text-violet-700 mt-2 space-y-1">
          <li>• Compliance and security requirements drive technical architecture decisions</li>
          <li>• Scalability needs determine infrastructure design and technology choices</li>
          <li>• Integration complexity influences development timeline and resource allocation</li>
          <li>
            • Custom development requirements shape the implementation approach and cost structure
          </li>
          <li>
            • Enterprise priorities help us recommend the optimal deployment and support model
          </li>
        </ul>
      </div>
    </div>
  );
};
